import {
    useEffect,
    useRef,
    type FC
} from "react";
import {
    type ViewStyle,
    Animated,
    Platform,
    View
} from "react-native";
import type IBubbleTypingProps from "./type";
import stylesheet, {
    BUBBLE_TYPING_REST_OPACITY,
    BUBBLE_TYPING_CYCLE_STEPS,
    BUBBLE_TYPING_DOT_COUNT,
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    useReducedMotion,
    MOTION_EASINGS
} from "../../../../helpers/motion";
import {
    useBubbleContext
} from "../../context";

const BubbleTyping: FC<IBubbleTypingProps> = ({
    customLocalize,
    customTheme,
    style,
    title,
    ...props
}) => {
    const context = useBubbleContext();

    const currentTheme = customTheme ?? context?.customTheme;

    const {
        typography,
        durations,
        iconSizes,
        radiuses,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(currentTheme);

    const {
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize ?? context?.customLocalize);

    const isReduced = useReducedMotion();

    const isWeb = Platform.OS === "web";

    const dots = useRef(Array.from({
        length: BUBBLE_TYPING_DOT_COUNT
    }, () => new Animated.Value(0))).current;

    const {
        container: containerDynamicStyle,
        webPulse: webPulseDynamicStyle,
        dot: dotDynamicStyle
    } = useStyles({
        lineHeight: typography[context?.currentSize.typography ?? "bodyMediumSize"].lineHeight,
        durations,
        iconSizes,
        radiuses,
        colors,
        spaces
    });

    useEffect(() => {
        dots.forEach((dot) => {
            dot.setValue(0);
        });

        if(isReduced || isWeb) {
            return;
        }

        const cycle = durations.long * BUBBLE_TYPING_CYCLE_STEPS;

        const animations = dots.map((dot, index) => {
            const delay = durations.short * index;

            return Animated.loop(Animated.sequence([
                Animated.delay(delay),
                Animated.timing(dot, {
                    easing: MOTION_EASINGS.inOut,
                    duration: durations.medium,
                    useNativeDriver: true,
                    isInteraction: false,
                    toValue: 1
                }),
                Animated.timing(dot, {
                    easing: MOTION_EASINGS.inOut,
                    duration: durations.medium,
                    useNativeDriver: true,
                    isInteraction: false,
                    toValue: 0
                }),
                Animated.delay(cycle - delay - durations.medium * 2)
            ]));
        });

        animations.forEach((animation) => {
            animation.start();
        });

        return () => {
            animations.forEach((animation) => {
                animation.stop();
            });
        };
    }, [
        durations.medium,
        durations.short,
        durations.long,
        isReduced,
        isWeb,
        dots
    ]);

    return <View
        {...props}
        aria-label={title ?? localize("typing")}
        style={[
            style,
            stylesheet.container,
            containerDynamicStyle
        ]}
        role="status"
    >
        {
            dots.map((dot, index) => isWeb ? <View
                style={[
                    dotDynamicStyle,
                    isReduced ? {
                        backgroundColor: colors.content.icon.mid
                    } : [
                        webPulseDynamicStyle,
                        {
                            animationDelay: `${durations.short * index}ms`
                        } as ViewStyle
                    ]
                ]}
                aria-hidden={true}
                key={index}
            /> : <Animated.View
                style={[
                    dotDynamicStyle,
                    isReduced ? {
                        backgroundColor: colors.content.icon.mid
                    } : {
                        transform: [
                            {
                                translateY: dot.interpolate({
                                    outputRange: [
                                        0,
                                        -spaces.spacingXs
                                    ],
                                    inputRange: [
                                        0,
                                        1
                                    ]
                                })
                            }
                        ],
                        opacity: dot.interpolate({
                            outputRange: [
                                BUBBLE_TYPING_REST_OPACITY,
                                1
                            ],
                            inputRange: [
                                0,
                                1
                            ]
                        })
                    }
                ]}
                aria-hidden={true}
                key={index}
            />)
        }
    </View>;
};
export default BubbleTyping;
